// useAoiAlertToggle — mutation hook wrapping
// POST /v1/saved-screens/:id/aoi-alert.
//
// The toggle is optimistic against the SavedView list cache: flip
// `aoi_alert_active` immediately, roll back on error. The matcher
// (services/intel-aoi-matcher) reads the underlying `saved_screen_aois.active`
// rows; the optimistic update only affects the rail UI.

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { AoiAlertToggleResponseSchema } from './types.js';
import {
  type AoiAlertToggleResponse,
  DEFAULT_SAVED_VIEW_ENDPOINTS,
  type SavedScreenSummary,
  type SavedViewEndpoints,
} from './types.js';
import type { SavedViewFetcher } from './useSavedViews.js';

export interface UseAoiAlertToggleOptions {
  /** Filter-schema entity tag — matches the cache key in useSavedViews. */
  entity: string;
  fetcher: SavedViewFetcher;
  endpoints?: SavedViewEndpoints;
}

export interface AoiAlertToggleInput {
  savedScreenId: string;
  active: boolean;
}

export type UseAoiAlertToggleResult = UseMutationResult<
  AoiAlertToggleResponse,
  Error,
  AoiAlertToggleInput
>;

export function useAoiAlertToggle({
  entity,
  fetcher,
  endpoints,
}: UseAoiAlertToggleOptions): UseAoiAlertToggleResult {
  const ep = { ...DEFAULT_SAVED_VIEW_ENDPOINTS, ...endpoints };
  const qc = useQueryClient();
  const listKey = ['saved-views', entity] as const;

  return useMutation<AoiAlertToggleResponse, Error, AoiAlertToggleInput>({
    mutationFn: async ({ savedScreenId, active }) => {
      const raw = await fetcher({
        url: ep.aoiAlertUrl.replace(':id', encodeURIComponent(savedScreenId)),
        method: 'POST',
        body: { active },
      });
      const parsed = AoiAlertToggleResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(`aoi-alert parse: ${parsed.error.message}`);
      }
      return parsed.data;
    },
    onMutate: async ({ savedScreenId, active }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const prev = qc.getQueryData<SavedScreenSummary[]>(listKey);
      qc.setQueryData<SavedScreenSummary[]>(listKey, (old) =>
        (old ?? []).map((s) => (s.id === savedScreenId ? { ...s, aoi_alert_active: active } : s)),
      );
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      const prev = (ctx as { prev?: SavedScreenSummary[] } | undefined)?.prev;
      if (prev) qc.setQueryData(listKey, prev);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: listKey });
    },
  });
}
